import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Cpu, Server, Bot, ShieldAlert, Wifi, Network, Activity as ActIcon, BarChart3,
  Zap, Check, X, Sun, ListTodo, AlertTriangle, ChevronRight, ImagePlus,
} from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";
import { HudPanel } from "@/components/command-center/hud/HudPanel";
import { AgentDetailDrawer, ApprovalConfirmDialog } from "@/components/command-center/hud/Dialogs";
import { useWorkspace } from "@/context/WorkspaceContext";
import { cn } from "@/lib/utils";

const STATUS_COLOR = { online: "#18c9ff", warning: "#ffb800", critical: "#ef4444", offline: "#475569", strained: "#ffb800", error: "#ef4444", idle: "#64748b" };

function Dot({ status }) {
  const c = STATUS_COLOR[status] || "#18c9ff";
  return <span className="inline-block w-2 h-2 rounded-full" style={{ background: c, boxShadow: `0 0 6px ${c}` }} />;
}

function Ring({ label, sub, value, metric }) {
  const color = metric?.up != null ? "#18c9ff" : "#64748b";
  const fraction = metric?.total > 0 ? Math.min(1, metric.up / metric.total) : 0;
  return (
    <div className="flex flex-col items-center gap-1" data-testid={`health-ring-${label.toLowerCase()}`}>
      <div className="relative w-[52px] h-[52px]">
        <svg viewBox="0 0 44 44" className="w-full h-full -rotate-90">
          <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(24,201,255,0.10)" strokeWidth="3" />
          <circle cx="22" cy="22" r="18" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
            strokeDasharray={`${2 * Math.PI * 18}`} strokeDashoffset={`${2 * Math.PI * 18 * (1 - fraction)}`}
            style={{ filter: `drop-shadow(0 0 4px ${color})` }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-num text-[10px] font-bold" style={{ color }}>{value}</div>
      </div>
      <div className="text-center leading-none">
        <div className="font-ui text-[10px] font-semibold uppercase tracking-wide text-jarvis-cyanBright/80">{label}</div>
        <div className="font-ui text-[9px] uppercase text-jarvis-cyanBright/40">{sub}</div>
      </div>
    </div>
  );
}

export function SystemHealthPanel() {
  const [h, setH] = useState(null);
  useEffect(() => {
    const load = () => apiGet("/system/health").then(setH).catch(() => {});
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, []);
  const fmt = (o) => (o?.up != null && o?.total != null ? `${o.up}/${o.total}` : "—");
  return (
    <HudPanel title="System Health" icon={Cpu} testId="panel-system-health"
      right={<span className="font-mono text-[9px] text-jarvis-green">CPU {h?.cpu ?? "–"}%</span>}>
      <div className="grid grid-cols-6 gap-x-0.5 gap-y-2">
        <Ring label="Servers" sub={h?.servers?.connected ? "reachable" : "Not registered"} value={h?.servers?.connected ? fmt(h.servers) : "—"} metric={h?.servers?.connected ? h.servers : undefined} />
        <Ring label="Agents" sub="app workers" value={fmt(h?.agents)} metric={h?.agents} />
        <Ring label="Devices" sub="Not connected" value="—" />
        <Ring label="Websites" sub="Not connected" value="—" />
        <Ring label="Sentinel" sub="Not connected" value="—" />
        <Ring label="Vault" sub={h?.vault_detail?.overdue ? `${h.vault_detail.overdue} overdue` : "encrypted"} value={h?.vault_detail ? String(h.vault_detail.items) : "—"} />
      </div>
    </HudPanel>
  );
}

export function VpsFleetPanel() {
  const [nodes, setNodes] = useState([]);
  useEffect(() => {
    const load = () => apiGet("/vps").then(setNodes).catch(() => {});
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, []);
  return (
    <HudPanel title="VPS Fleet" icon={Server} testId="panel-vps-fleet"
      right={<span className="font-mono text-[9px] text-slate-500" data-testid="vps-connection-state">{nodes.length ? "Collector data" : "Not connected"}</span>}>
      <div className="space-y-2.5">
        {nodes.length === 0 && <div className="font-mono text-[10px] text-slate-500" data-testid="vps-not-connected">No authenticated VPS telemetry collector is connected.</div>}
        {nodes.map((n) => (
          <div key={n.id} className="flex items-center gap-2" data-testid={`vps-row-${n.id}`}>
            <Dot status={n.status} />
            <div className="min-w-0 w-20">
              <div className="font-mono text-[11px] text-slate-200 truncate">{n.name}</div>
              <div className="font-mono text-[8px] uppercase text-slate-500">{n.status}</div>
            </div>
            <div className="ml-auto text-right">
              <div className="font-mono text-[11px] text-jarvis-cyanBright">{n.cpu}%</div>
              <div className="font-mono text-[8px] uppercase text-slate-500">load</div>
            </div>
          </div>
        ))}
      </div>
    </HudPanel>
  );
}

export function NetworkSecurityPanel() {
  const [net, setNet] = useState(null);
  const [h, setH] = useState(null);
  useEffect(() => {
    const load = () => {
      apiGet("/telemetry").then(setNet).catch(() => {});
      apiGet("/system/health").then(setH).catch(() => {});
    };
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, []);
  return (
    <HudPanel title="Network & Security" icon={Wifi} testId="panel-network-security">
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-md border border-white/8 bg-black/20 px-2.5 py-2">
          <div className="flex items-center gap-1.5 mb-0.5"><Wifi className="w-3 h-3 text-slate-500" />
            <span className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Wi-Fi</span></div>
          <div className="font-mono text-[10px] text-slate-500">Not connected</div>
        </div>
        <div className="rounded-md border border-white/8 bg-black/20 px-2.5 py-2">
          <div className="flex items-center gap-1.5 mb-0.5"><Network className="w-3 h-3 text-jarvis-cyan" />
            <span className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Network</span></div>
          <div className="font-mono text-[10px] text-jarvis-cyanBright">↓{net?.net_recv_kbs ?? "–"} ↑{net?.net_sent_kbs ?? "–"} <span className="text-slate-500">KB/s</span></div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center rounded-md border border-jarvis-green/20 bg-black/20 py-1.5">
          <div className="font-mono text-[8px] uppercase text-slate-500">Critical</div>
          <div className="font-mono text-base font-bold text-jarvis-green">{h?.alerts?.critical ?? "—"}</div>
        </div>
        <div className="text-center rounded-md border border-jarvis-amber/20 bg-black/20 py-1.5">
          <div className="font-mono text-[8px] uppercase text-slate-500">Warnings</div>
          <div className="font-mono text-base font-bold text-jarvis-amber">{h?.alerts?.warning ?? "—"}</div>
        </div>
        <div className="text-center rounded-md border border-white/8 bg-black/20 py-1.5">
          <div className="font-mono text-[8px] uppercase text-slate-500">Monitoring</div>
          <div data-testid="security-monitoring-state" className="font-mono text-[9px] text-slate-500 mt-1">Not connected</div>
        </div>
      </div>
    </HudPanel>
  );
}

export function ActiveAgentsPanel({ onOpenAgents }) {
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const { workspace } = useWorkspace();
  useEffect(() => {
    const q = workspace && workspace !== "global" ? `?workspace=${workspace}` : "";
    const load = () => apiGet(`/agents${q}`).then((a) => setAgents(a)).catch(() => {});
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [workspace]);
  const shown = agents.slice(0, 5);
  return (
    <HudPanel title={`Active Agents (${agents.length})`} icon={Bot} testId="panel-active-agents"
      right={<button onClick={onOpenAgents} data-testid="agents-open-btn" className="font-ui text-[11px] uppercase tracking-wide text-jarvis-cyanBright/50 hover:text-jarvis-cyanBright flex items-center">all <ChevronRight className="w-3 h-3" /></button>}>
      <div className="space-y-1.5">
        {shown.length === 0 && <div className="font-ui text-[13px] text-jarvis-cyanBright/40">No active agents</div>}
        {shown.map((a) => (
          <button key={a.id} onClick={() => setSelected(a)}
            className="w-full flex items-center justify-between text-left px-2 py-1.5 -mx-1 rounded hover:bg-jarvis-cyan/[0.06] focus:outline-none focus-visible:ring-1 focus-visible:ring-jarvis-cyan transition-colors group"
            data-testid={`active-agent-${a.name}`}>
            <div className="flex items-center gap-2 min-w-0">
              <Dot status={a.status} />
              <span className="text-[14px] text-jarvis-cyanBright/85 truncate">{a.name}</span>
            </div>
            <span className="flex items-center gap-1 font-num text-[11px] text-jarvis-cyanBright/45 shrink-0">
              {a.status}<ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100" />
            </span>
          </button>
        ))}
      </div>
      <AgentDetailDrawer agent={selected} onClose={() => setSelected(null)} />
    </HudPanel>
  );
}

const RISK_COLOR = { LOW: "#18c9ff", MEDIUM: "#ffd34e", HIGH: "#ff3f58" };
export function ApprovalsPanel() {
  const [items, setItems] = useState([]);
  const [confirm, setConfirm] = useState(null); // { ap, decision }
  const [submitting, setSubmitting] = useState(false);
  const load = () => apiGet("/approvals").then(setItems).catch(() => {});
  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, []);

  const runDecision = async () => {
    if (!confirm || submitting) return;
    const { ap, decision } = confirm;
    setSubmitting(true);
    try {
      await apiPost(`/approvals/${ap.id}/${decision}`);
      setItems((p) => p.filter((a) => a.id !== ap.id));
      toast[decision === "approve" ? "success" : "message"](`${decision === "approve" ? "Approved" : "Denied"}: ${ap.action}`);
      setConfirm(null);
    } catch (e) {
      const msg = String(e?.message || "");
      if (msg.includes("404") || msg.toLowerCase().includes("not found")) {
        toast.message("This request was already resolved.");
        setItems((p) => p.filter((a) => a.id !== ap.id));
        setConfirm(null);
      } else {
        toast.error("Action failed — please retry.");
        load();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <HudPanel title={`Approvals (${items.length})`} icon={ShieldAlert} accent="amber" testId="panel-approvals">
      <div className="space-y-2 max-h-44 overflow-y-auto pr-0.5">
        {items.length === 0 && <div data-testid="approvals-not-connected" className="font-ui text-[13px] text-jarvis-cyanBright/40">Approval execution not connected</div>}
        <AnimatePresence>
          {items.map((ap) => {
            const busy = submitting && confirm?.ap.id === ap.id;
            return (
              <motion.div key={ap.id} layout initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }}
                className="border border-jarvis-cyan/12 bg-black/25 px-2.5 py-2 hud-chamfer" data-testid={`approval-${ap.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[13px] text-jarvis-cyanBright/90 leading-snug truncate">{ap.action}</div>
                    <div className="font-num text-[10px] text-jarvis-cyanBright/45 truncate">{ap.agent} · <span style={{ color: RISK_COLOR[ap.risk] }}>{ap.risk}</span></div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => setConfirm({ ap, decision: "approve" })} disabled={busy} data-testid={`approval-approve-${ap.id}`}
                      className="flex items-center gap-1 font-ui text-[11px] font-semibold uppercase px-2 py-1 border border-jarvis-green/40 text-jarvis-green hover:bg-jarvis-green/20 disabled:opacity-40 hud-chamfer">
                      <Check className="w-3 h-3" /> Approve
                    </button>
                    <button onClick={() => setConfirm({ ap, decision: "deny" })} disabled={busy} data-testid={`approval-deny-${ap.id}`}
                      className="flex items-center gap-1 font-ui text-[11px] font-semibold uppercase px-2 py-1 border border-jarvis-red/40 text-jarvis-red hover:bg-jarvis-red/20 disabled:opacity-40 hud-chamfer">
                      <X className="w-3 h-3" /> Deny
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
      <ApprovalConfirmDialog
        approval={confirm?.ap}
        decision={confirm?.decision}
        submitting={submitting}
        onCancel={() => !submitting && setConfirm(null)}
        onConfirm={runDecision}
      />
    </HudPanel>
  );
}

export function RecentActivityPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const load = () => apiGet("/activity?limit=6").then(setItems).catch(() => {});
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, []);
  const time = (ts) => ts ? new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "--:--";
  return (
    <HudPanel title="Recent Activity" icon={ActIcon} testId="panel-recent-activity">
      <div className="space-y-1.5">
        {items.map((a) => (
          <div key={`${a.ts}:${a.workspace}:${a.text}`} className="flex items-start gap-2 font-mono text-[10px]">
            <span className="text-slate-600 shrink-0">{time(a.ts)}</span>
            <span className="mt-1 w-1 h-1 rounded-full bg-jarvis-cyan/70 shrink-0" />
            <span className="text-slate-400 leading-snug">{a.text}</span>
          </div>
        ))}
      </div>
    </HudPanel>
  );
}

export function AiUsagePanel() {
  const [u, setU] = useState(null);
  useEffect(() => {
    const load = () => apiGet("/ai-usage").then(setU).catch(() => {});
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);
  return (
    <HudPanel title="AI Usage" icon={BarChart3} testId="panel-ai-usage"
      right={<span data-testid="usage-billing-status" className="font-mono text-[9px] text-slate-500">Billing not connected</span>}>
      <div className="font-mono text-[9px] text-slate-500 mb-3" data-testid="usage-source">Requests reserved today · UTC</div>
      <div className="space-y-2 text-xs text-slate-400">
        <div className="flex justify-between"><span>Chat, triage & checks</span><span data-testid="usage-chat-count" className="font-num text-jarvis-cyan">{u?.requests_today?.chat ?? "—"}</span></div>
        <div className="flex justify-between"><span>Image requests</span><span data-testid="usage-image-count" className="font-num text-jarvis-cyan">{u?.requests_today?.images ?? "—"}</span></div>
      </div>
      <div className="font-mono text-[9px] text-slate-500 border-t border-white/8 mt-3 pt-2" data-testid="usage-cost-not-connected">Token counts and provider costs: Not connected</div>
    </HudPanel>
  );
}

export function QuickCommandsPanel({ onBriefing, onStatus, onStrained, onTasks, onImage }) {
  const items = [
    { icon: Sun, label: "Morning Briefing", action: onBriefing },
    { icon: ActIcon, label: "System Status", action: onStatus },
    { icon: AlertTriangle, label: "Strained Agents", action: onStrained },
    { icon: ListTodo, label: "Active Tasks", action: onTasks },
    { icon: ImagePlus, label: "Generate Image", action: onImage },
  ];
  return (
    <HudPanel title="Quick Commands" icon={Zap} testId="panel-quick-commands">
      <div className="grid grid-cols-5 gap-2">
        {items.map((it) => (
          <button key={it.label} onClick={it.action} data-testid={`quick-cmd-${it.label.split(" ")[0].toLowerCase()}`}
            className="flex flex-col items-center gap-1.5 rounded-md border border-jarvis-cyan/15 bg-black/20 py-2.5 text-slate-300 hover:text-jarvis-cyanBright hover:border-jarvis-cyan/40 transition-colors">
            <it.icon className="w-4 h-4" />
            <span className="font-mono text-[8px] uppercase tracking-wider text-center leading-tight">{it.label}</span>
          </button>
        ))}
      </div>
    </HudPanel>
  );
}
